import { totalCustomerContractUser } from "../../graphql/queries/totalCustomerContractUser";
import { AssetsTotal } from "../../graphql/queries/assetsTotal";
import { UserRegistrationListTotal } from "../../graphql/queries/userRegistrationListTotal";
import { useQuery, DocumentNode } from "@apollo/client";
import _ from "lodash";
import { CircularProgress, Typography } from "@mui/joy";
import { t } from "i18next";
import { WidgetConfig } from "../DashboardWidgetConfig";
import WidgetHeader from "../WidgetHeader";
import { useKeycloak } from "@react-keycloak/web";
import { getUserResources } from "@dreebit-registry/vsm-microservice-components";
import { useNavigate } from "react-router-dom";
import { IOT_STATUS_CONFIG } from "../../config/iotConnectionStatusConfig";
import { clearAssetListPersistedFilters } from "../../hooks/useSyncedAssetListParams";

export type KPIType = 'totalCustomers' | 'totalUsers' | 'connectedPumps' | 'totalAssetCount';

interface KPISubtitle {
  query: DocumentNode;
  variables?: Record<string, unknown>;
  dataPath: string;
  template: string; // use {value} as placeholder
}

interface KPIDefinition {
  title: string;
  query: DocumentNode;
  variables?: Record<string, unknown>;
  dataPath: string;
  subtitle?: KPISubtitle;
}

const KPI_DEFINITIONS: Record<KPIType, KPIDefinition> = {
  totalAssetCount: {
    title: 'Total Assets',
    query: AssetsTotal,
    dataPath: 'searchAssets.total',
  },
  totalCustomers: {
    title: 'Total Customers',
    query: totalCustomerContractUser,
    dataPath: 'clientList.total',
  },
  totalUsers: {
    title: 'Total Users',
    query: totalCustomerContractUser,
    dataPath: 'userList.total',
  },
  connectedPumps: {
    title: 'Connected Assets',
    query: AssetsTotal,
    variables: { filter: { iotConnectionStatus: { notIn: ["NEVER_CONNECTED"] } } },
    dataPath: 'searchAssets.total',
    subtitle: {
      query: AssetsTotal,
      dataPath: 'searchAssets.total',
      template: 'of {value} assets',
    },
  },
};

interface KPIWidgetProps {
  onDelete: () => void;
  onEdit: () => void;
  config?: WidgetConfig;
}

const KPIWidget: React.FC<KPIWidgetProps> = ({ onDelete, onEdit, config }) => {
  const kpiType = (config?.kpiType as KPIType) || 'totalCustomers';
  const kpiDefinition = KPI_DEFINITIONS[kpiType];

  const { keycloak } = useKeycloak();
  const crmResources = getUserResources(keycloak.token, 'otto_crm');
  const showRegistrationInfo =
    kpiType === 'totalUsers' && crmResources.includes('administrateUserRegistrations');
  const navigate = useNavigate();

  const clickHandler: (() => void) | undefined = (() => {
    if (kpiType === 'totalAssetCount') {
      return () => {
        clearAssetListPersistedFilters();
        navigate('/assets');
      };
    }
    if (kpiType === 'connectedPumps') {
      return () => {
        const params = new URLSearchParams();
        IOT_STATUS_CONFIG
          .filter(s => s.status !== 'NEVER_CONNECTED')
          .forEach(s => params.append('iotConnectionStatus', s.status));
        navigate(`/assets?${params.toString()}`);
      };
    }
    if (showRegistrationInfo) {
      return () => window.open('/crm/registrations', '_blank');
    }
    return undefined;
  })();

  const { data, loading, error } = useQuery(kpiDefinition.query, {
    variables: kpiDefinition.variables || {},
  });

  const { data: subtitleData } = useQuery(
    kpiDefinition.subtitle?.query || kpiDefinition.query,
    {
      variables: kpiDefinition.subtitle?.variables || {},
      skip: !kpiDefinition.subtitle,
    },
  );

  const { data: registrationData } = useQuery(UserRegistrationListTotal, {
    skip: !showRegistrationInfo,
  });
  const registrationCount = _.get(registrationData, 'userRegistrationList.total', 0);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        <CircularProgress size="sm" />
      </div>
    );
  }

  if (error) {
    return <p style={{ fontSize: "0.875rem", color: "#d32f2f" }}>Error loading data.</p>;
  }

  const value = _.get(data, kpiDefinition.dataPath, 0);
  const subtitleValue = kpiDefinition.subtitle
    ? _.get(subtitleData, kpiDefinition.subtitle.dataPath, 0)
    : null;
  const subtitleText = kpiDefinition.subtitle && subtitleValue != null
    ? t(kpiDefinition.subtitle.template).replace('{value}', String(subtitleValue))
    : null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
      }}
    >
      <WidgetHeader
        title={t(kpiDefinition.title)}
        onDelete={onDelete}
        onEdit={onEdit}
      />

      <div
        className={clickHandler ? "no-drag" : undefined}
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          flex: 1,
          paddingLeft: "10px",
          cursor: clickHandler ? "pointer" : "default",
        }}
        onClick={clickHandler}
      >
        <Typography level="titleExtraLarge" sx={{ color: "#333", fontWeight: 700 }}>
          {value}
        </Typography>
        {showRegistrationInfo ? (
          <Typography level="bodySmall" sx={{ fontWeight: "bold", color: "#666" }}>
            {t('{{count}} new registrations', { count: registrationCount })}
          </Typography>
        ) : (
          subtitleText && (
            <Typography level="bodySmall" sx={{ fontWeight: "bold", color: "#666" }}>
              {subtitleText}
            </Typography>
          )
        )}
      </div>
    </div>
  );
};

export default KPIWidget;
